import { ROUTES } from "@app/constants";
import { useAttachReport } from "@entities/brief/lib/hooks/useAttachReport";
import { useGetMe } from "@entities/Onboarding/lib/hooks/useGetMe";
import { useEditReportDataFields } from "@entities/reports/lib/hooks/customFields/useEditReportDataFields";
import { useAddMediaReport } from "@entities/reports/lib/hooks/useAddMediaReport";
import { useDeleteMedia } from "@entities/reports/lib/hooks/useDeleteMedia";
import { useEditCost } from "@entities/reports/lib/hooks/useEditCost";
import { useExportReport } from "@entities/reports/lib/hooks/useExportReport";
import { useGetReportDetail } from "@entities/reports/lib/hooks/useGetReportDetail";
import { AddMediaForm } from "@entities/reports/ui/AddMediaForm";
import { ReportUpdateConfigReadonly } from "@entities/reports/ui/ReportUpdateConfigReadonly";
import type { ReportUpdateConfigValue } from "@entities/reports/lib/utils/reportUpdateConfig";
import { CustomizeReportFields } from "@features/customizeReportFields/CustomizeReportFields";
import {
  NonTrackableCard,
  NonTrackableLinksModal,
} from "@features/NonTrackableReportMedias";
import { ReportHeader } from "@features/ReportHeader";
import { ReportMediaTable } from "@features/ReportMediaTable";
import { useBriefStore } from "@pages/layout/BriefLayout/lib/useLayoutStore";
import { ToastService } from "@shared/lib/services";
import { ReportType } from "@shared/lib/types/reports";
import { Alert, Button, Modal } from "@shared/ui";
import { ReportAggregatedData } from "@widgets/ReportAggregatedData";
import { Drawer } from "antd";
import { FilePlus, RefreshCcw } from "lucide-react";
import { useEffect, useState } from "react";
import { useMatch, useParams } from "react-router-dom";
import { BriefReportWaitingState } from "./BriefReportWaitingState";
import styles from "./styles.module.scss";

export function ReportDetailPage() {
  const { exportReport, isExporting } = useExportReport();
  const { reportId: reportIdParams } = useParams();
  const { brief, briefId } = useBriefStore();
  const isInBrief = Boolean(useMatch(ROUTES.BRIEF_V2_REPORT));

  const reportId = reportIdParams || brief?.reportId || "";

  const [pageNo, setPageNo] = useState(1);
  const [pageSize] = useState(10);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isNonTrackableModalOpen, setIsNonTrackableModalOpen] = useState(false);
  const [isCustomizeFieldsModalOpen, setIsCustomizeFieldsModalOpen] =
    useState(false);

  const { userDetails } = useGetMe();
  const isAddContentButtonDisabled = false;
  const isDeleteMediaDisabled = false;

  const {
    report: aggregatedData,
    isFetching: isFetchingAggregatedData,
    refetch: refetchAggregatedData,
  } = useGetReportDetail(reportId, false, undefined, 10, 1);

  const {
    report,
    isFetching,
    isError,
    refetch,
    queryKey: reportQueryKey,
  } = useGetReportDetail(reportId, false, undefined, pageSize, pageNo);

  const { report: nonTrackableReport, refetch: refetchNonTrackableReport } =
    useGetReportDetail(reportId, true, undefined, Number.MAX_SAFE_INTEGER, 1);

  const [showReportMsg, setShowReportMsg] = useState(false);

  useEffect(() => {
    setShowReportMsg(
      report?.data?.some((val) => val.mediaDataStatus === "Pending") || false
    );
  }, [report]);

  const { editCost, isEditing, isEdited } = useEditCost(
    reportId,
    reportQueryKey
  );

  const {
    deleteMedia,
    isAdding: isDeletingMedia,
    isAdded: isDeletedMedia,
    variables: deleteMediaVariables,
  } = useDeleteMedia(reportId, () => {
    ToastService.success("Content removed successfully");
    refetch();
    refetchAggregatedData();
    refetchNonTrackableReport();
  });

  const [editFieldProps, setEditFieldProps] = useState<{
    reportMediaId: string;
    key: string;
    value?: string | number;
  }>({
    reportMediaId: "",
    key: "",
    value: undefined,
  });

  const { addMedia, isAdding, isAdded } = useAddMediaReport(reportId, () => {
    setIsModalOpen(false);
    setTimeout(() => {
      refetch();
      refetchAggregatedData();
      refetchNonTrackableReport();
    }, 1000);
  });

  const {
    editReportDataFields,
    isEdited: isEditedReportDataFields,
    isEditing: isEditingReportDataFields,
  } = useEditReportDataFields(reportId);

  const { attachReport, isAttaching, isAttached } = useAttachReport();

  useEffect(() => {
    if (isAdded) {
      setIsModalOpen(false);
      setTimeout(() => {
        refetch();
        refetchAggregatedData();
        refetchNonTrackableReport();
      }, 1000);
    }
  }, [isAdded, refetch, refetchAggregatedData, refetchNonTrackableReport]);

  useEffect(() => {
    if (isAttached) {
      ToastService.success("Report created successfully");
    }
  }, [isAttached]);

  useEffect(() => {
    if (isEditedReportDataFields) {
      setEditFieldProps({
        reportMediaId: "",
        key: "",
        value: undefined,
      });
      ToastService.success("Data field updated successfully");
      refetch();
    }
  }, [isEditedReportDataFields, refetch]);

  useEffect(() => {
    if (!isDeletedMedia) {
      return;
    }

    const remainingOnPage = (report?.data?.length ?? 0) - 1;
    if (remainingOnPage <= 0 && pageNo > 1) {
      setPageNo((current) => current - 1);
    }
  }, [isDeletedMedia, report?.data?.length, pageNo]);

  const handleAddContent = () => {
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const handleSubmit = (values: { link: string }) => {
    addMedia({ link: values.link });
  };

  const handleEditCost = (reportMediaId: string, cost: number) => {
    editCost({ reportMediaId, cost });
  };

  const isWholeDataEmpty =
    report?.data?.length === 0 && pageNo === 1 && !isFetching;

  const renderAddContentModal = () => (
    <Modal
      open={isModalOpen}
      onCancel={handleCancel}
      title="Add Content to Report"
      footer={null}
      destroyOnClose
    >
      <AddMediaForm onSubmit={handleSubmit} isSubmitting={isAdding} />
    </Modal>
  );

  const renderNonTrackableSection = () =>
    nonTrackableReport?.data && nonTrackableReport.data.length > 0 ? (
      <>
        <NonTrackableCard
          nonTrackableLinks={nonTrackableReport.data}
          onViewLinks={() => setIsNonTrackableModalOpen(true)}
        />
        <NonTrackableLinksModal
          open={isNonTrackableModalOpen}
          onClose={() => setIsNonTrackableModalOpen(false)}
          nonTrackableLinks={nonTrackableReport.data}
        />
      </>
    ) : null;

  const renderCustomizeFieldsDrawer = () => (
    <Drawer
      open={isCustomizeFieldsModalOpen}
      onClose={() => setIsCustomizeFieldsModalOpen(false)}
      title="Customize Fields"
      width={480}
    >
      {report?.report && (
        <CustomizeReportFields
          reportId={reportId}
          report={report.report}
          onClose={() => {
            setIsCustomizeFieldsModalOpen(false);
            refetch();
          }}
        />
      )}
    </Drawer>
  );

  const renderPendingAlert = () =>
    showReportMsg ? (
      <Alert
        type="info"
        message="Content added. Report is being fetched, please wait..."
        showIcon
        action={
          <Button
            onClick={() => refetch()}
            icon={<RefreshCcw size={16} />}
            type="link"
            size="small"
          >
            Refresh
          </Button>
        }
      />
    ) : null;

  const reportMeta = report?.report ?? aggregatedData?.report;

  const renderUpdateSchedule = () => {
    if (reportMeta?.version !== "v2") {
      return null;
    }

    return (
      <ReportUpdateConfigReadonly
        updateTillDate={reportMeta.configV2.updateTillDate}
        updateForIntervalHourFrequency={
          reportMeta.configV2.updateForIntervalHourFrequency
        }
      />
    );
  };

  const handleCreateBriefReport = (schedule: ReportUpdateConfigValue) => {
    attachReport({
      briefId,
      updateForIntervalHourFrequency: schedule.updateForIntervalHourFrequency,
      updateTillDate: schedule.updateTillDate,
    });
  };

  if (!reportId) {
    return (
      <div className={styles.container}>
        <BriefReportWaitingState
          onCreateReport={handleCreateBriefReport}
          isCreating={isAttaching}
        />
      </div>
    );
  }

  if (isError) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          <h3 className={styles.emptyStateTitle}>Invalid report</h3>
        </div>
      </div>
    );
  }

  if (isFetchingAggregatedData && !aggregatedData) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>Loading report...</div>
      </div>
    );
  }

  if (isWholeDataEmpty) {
    return (
      <div className={styles.container}>
        <ReportHeader
          reportName={aggregatedData?.report?.name || ""}
          reportType={aggregatedData?.report?.type || "Custom"}
          reportId={reportId}
          isInBrief={isInBrief}
          onAddContent={handleAddContent}
          onCustomizeFields={() => setIsCustomizeFieldsModalOpen(true)}
          onExportReport={() => exportReport(reportId)}
          isAddContentButtonDisabled={isAddContentButtonDisabled}
          isExporting={isExporting}
        />
        {renderPendingAlert()}
        {renderUpdateSchedule()}
        {renderNonTrackableSection()}
        <div className={styles.emptyState}>
          <div className={styles.emptyStateIcon}>
            <FilePlus size={32} className={styles.emptyStateIconColor} />
          </div>
          <h3 className={styles.emptyStateTitle}>No content added yet</h3>
          <p className={styles.emptyStateDescription}>
            This custom report is currently empty. Add content from campaigns or
            creator profiles to start analyzing performance.
          </p>
          <Button
            type="primary"
            className={styles.emptyStateButton}
            icon={<FilePlus size={16} />}
            onClick={handleAddContent}
            disabled={isAddContentButtonDisabled}
          >
            Add Content to Report
          </Button>
        </div>
        {renderAddContentModal()}
        {renderCustomizeFieldsDrawer()}
      </div>
    );
  }

  return (
    <div className={styles.container} style={{ paddingBottom: 20 }}>
      <ReportHeader
        reportName={report?.report?.name || ""}
        reportType={report?.report?.type || "Custom"}
        reportId={reportId}
        isInBrief={isInBrief}
        onAddContent={handleAddContent}
        onCustomizeFields={() => setIsCustomizeFieldsModalOpen(true)}
        onExportReport={() => exportReport(reportId)}
        isAddContentButtonDisabled={isAddContentButtonDisabled}
        isExporting={isExporting}
      />
      {renderPendingAlert()}
      {renderUpdateSchedule()}
      {aggregatedData?.aggregateData && (
        <ReportAggregatedData
          aggregatedData={aggregatedData.aggregateData}
          totalMedia={aggregatedData.pagination?.totalCount ?? 0}
          timeSeriesData={aggregatedData.timeseriesData}
          reportVersion={aggregatedData.report?.version}
        />
      )}
      <ReportMediaTable
        reportMedia={report?.data || []}
        isFetching={isFetching}
        pageNo={pageNo}
        pageSize={pageSize}
        totaldata={aggregatedData?.pagination?.totalCount || 0}
        onPageChange={setPageNo}
        totalPages={aggregatedData?.pagination?.totalPages || 0}
        reportName={report?.report?.name || ""}
        reportId={reportId}
        onEditCost={handleEditCost}
        isEditing={isEditing}
        isEdited={isEdited}
        visibleCustomFields={report?.report?.visibileCustomFieldsV2 || []}
        dataFieldEditProps={editFieldProps}
        setDataFieldEditProps={setEditFieldProps}
        onSaveReportDataField={({ itemId, dataField }) => {
          editReportDataFields({ itemId, dataField });
        }}
        isSavingDataField={isEditingReportDataFields}
        onDeleteMedia={(mediaId) => deleteMedia({ mediaId })}
        isDeletingMedia={isDeletingMedia}
        deletingMediaId={deleteMediaVariables?.mediaId}
        isDeleteMediaDisabled={isDeleteMediaDisabled}
        reportType={report?.report?.type || ReportType.Custom}
      />
      {renderNonTrackableSection()}
      {renderAddContentModal()}
      {renderCustomizeFieldsDrawer()}
    </div>
  );
}
